// Named contacts with roles (Session ASN, Phase 9, D86): the client half. The role set and its labels MIRROR
// server/src/modules/contacts/contacts.ts, bound by contactRoleParity.test.ts, so the office panel and the server (and
// Guvnor, which reads the server's labels) can never name a role two different ways.
//
// A contact is a record the office reads and uses by hand. Nothing that sends an email reads it.
import { apiRequest } from "./api";

export const CONTACT_ROLES = ["billing", "site", "managing_agent", "signatory", "emergency", "other"] as const;
export type ContactRole = (typeof CONTACT_ROLES)[number];

export const CONTACT_ROLE_LABEL: Record<ContactRole, string> = {
  billing: "Billing",
  site: "On-site / facilities",
  managing_agent: "Managing agent",
  signatory: "Signs agreements",
  emergency: "Emergency / keyholder",
  other: "Other",
};

export interface Contact {
  id: string;
  customerId: string;
  customerName: string | null;
  siteId: string | null;
  siteName: string | null;
  name: string;
  role: ContactRole;
  roleOther: string | null;
  roleLabel: string;
  email: string | null;
  phone: string | null;
  notes: string | null;
  linkedCustomer: { id: string; name: string } | null;
  // Session AUH (Phase 7): never chase this person; never chase them firmly. Optional for an older response.
  doNotChase?: boolean;
  doNotChaseHard?: boolean;
  // Session CR1: this exact address is a confirmed correspondent of its customer (in force now), and when that lapses.
  confirmedCorrespondent?: boolean;
  correspondentConfirmedAt?: string | null;
  correspondentExpiresAt?: string | null;
  archived: boolean;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ContactInput {
  siteId?: string | null;
  name?: string;
  role?: ContactRole;
  roleOther?: string | null;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
  linkedCustomerId?: string | null;
  doNotChase?: boolean;
  doNotChaseHard?: boolean;
}

// Session CR1: the roles that may be confirmed as a correspondent. MIRRORS CORRESPONDENT_ROLES on the server.
export const CORRESPONDENT_ROLES: readonly ContactRole[] = ["billing", "site", "managing_agent", "signatory"];

export interface CorrespondentPreview {
  preview: true;
  contactId: string;
  wouldExpireAt: string;
  effect: string;
  warnings: string[];
}

export type ContactListQuery = { customerId: string } | { siteId: string } | { linkedCustomerId: string };

export const contactsApi = {
  // Always read fresh (noCache): the list feeds the panel the office edits, and a copy cached before a change made
  // elsewhere (Guvnor, another tab) would put a stale contact back on screen.
  list: async (query: ContactListQuery, includeArchived = false): Promise<Contact[]> => {
    const qs = new URLSearchParams(query as Record<string, string>);
    if (includeArchived) qs.set("includeArchived", "true");
    const res = await apiRequest<{ contacts: Contact[] }>(`/contacts?${qs.toString()}`, { noCache: true });
    return res.contacts;
  },
  create: (body: ContactInput & { customerId: string; name: string; role: ContactRole }) =>
    apiRequest<Contact>("/contacts", { method: "POST", body: JSON.stringify(body) }),
  update: (id: string, body: ContactInput) => apiRequest<Contact>(`/contacts/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
  archive: (id: string) => apiRequest<{ ok: true; archived: true }>(`/contacts/${encodeURIComponent(id)}`, { method: "DELETE" }),
  restore: (id: string) => apiRequest<Contact>(`/contacts/${encodeURIComponent(id)}/restore`, { method: "POST" }),
  confirmCorrespondentPreview: (id: string) =>
    apiRequest<CorrespondentPreview>(`/contacts/${encodeURIComponent(id)}/confirm-correspondent`, { method: "POST", body: JSON.stringify({ preview: true }) }),
  confirmCorrespondent: (id: string) =>
    apiRequest<Contact>(`/contacts/${encodeURIComponent(id)}/confirm-correspondent`, { method: "POST", body: JSON.stringify({ preview: false }) }),
  revokeCorrespondent: (id: string) => apiRequest<Contact>(`/contacts/${encodeURIComponent(id)}/confirm-correspondent`, { method: "DELETE" }),
  deletePermanently: (id: string) => apiRequest<{ ok: true; deleted: true }>(`/contacts/${encodeURIComponent(id)}?permanent=true`, { method: "DELETE" }),
};
